import type { Modifier } from '@popperjs/core';

function cssPolygon(polygon: number[][]) {
	return `polygon(${polygon
		.map((x) => x.map((y) => `${y}px`).join(' '))
		.join(', ')})`;
}

interface NotchedPopperModifierOptions {
	/**
	 * Distance from border box to clip into popper element. Set equal to the
	 * popper element's border width to clip away its border.
	 *
	 * @default 0
	 */
	notchDepth: number;
}

const notchedPopperModifierBase: Modifier<
	'notched',
	NotchedPopperModifierOptions
> = {
	name: 'notched',
	enabled: true,
	phase: 'beforeWrite',
	requires: ['popperOffsets'],
	fn: ({ state, options: { notchDepth = 0 } }) => {
		if (state.modifiersData.popperOffsets === undefined) {
			console.warn('popperOffsets is not defined. Cannot make notch.');
			return;
		}

		const referenceLeft = state.rects.reference.x;
		const referenceRight = referenceLeft + state.rects.reference.width;
		const referenceTop = state.rects.reference.y;

		const popperLeft = state.modifiersData.popperOffsets.x;
		const popperTop = state.modifiersData.popperOffsets.y;
		const popperWidth = state.rects.popper.width;
		const popperHeight = state.rects.popper.height;

		/** Additional space to include to account for shadows. */
		const outsetForShadow = 30;

		const outsetTop = -outsetForShadow;
		const outsetBottom = popperHeight + outsetForShadow;
		const outsetLeft = -outsetForShadow;
		const outsetRight = popperWidth + outsetForShadow;

		const notchLeft = referenceLeft - popperLeft;
		const notchRight = referenceRight - popperLeft;

		/**
		 * Is the notch in the top (true) or bottom (false) of the popper.
		 *
		 * Note: This code does not support notches in the sides of the popper.
		 */
		const notchIsOnTop = referenceTop < popperTop;

		// Cut the notch deep enough that the popper's border is clipped away.
		const notchBottom = notchIsOnTop ? notchDepth : popperHeight - notchDepth;

		state.styles.popper!.clipPath = cssPolygon(
			notchIsOnTop
				? [
						...(notchLeft > outsetLeft
							? [
									[outsetLeft, outsetTop],
									[notchLeft, outsetTop],
									[notchLeft, notchBottom],
								]
							: [[outsetLeft, notchBottom]]),
						...(notchRight < outsetRight
							? [
									[notchRight, notchBottom],
									[notchRight, outsetTop],
									[outsetRight, outsetTop],
								]
							: [[outsetRight, notchBottom]]),
						[outsetRight, outsetBottom],
						[outsetLeft, outsetBottom],
					]
				: [
						[outsetLeft, outsetTop],
						[outsetRight, outsetTop],
						...(notchRight < outsetRight
							? [
									[outsetRight, outsetBottom],
									[notchRight, outsetBottom],
									[notchRight, notchBottom],
								]
							: [[outsetRight, notchBottom]]),
						...(notchLeft > outsetLeft
							? [
									[notchLeft, notchBottom],
									[notchLeft, outsetBottom],
									[outsetLeft, outsetBottom],
								]
							: [[outsetLeft, notchBottom]]),
					]
		);
	},
};

export function notchedPopperModifier(options?: NotchedPopperModifierOptions) {
	return { ...notchedPopperModifierBase, options };
}
